// Pure helpers for the sub group (multi-venue) pay-later order path in
// contact-details/index.vue. Extracted so the payload shape and the post-paid
// routing decision can be unit-tested without mounting the (pug + mixins) SFC —
// same approach as views/venue-sub-group/storeOrderNumbers.js.
//
// BUG-1 background: the pay-later `else` branch used to always call the single-store
// createOrder()/addOrder() with only state.venueId, silently dropping every other
// store's cart. These helpers build the whole-cart sub group payload instead.

import { addVenueSubGroupOrder } from '@/api/product'

// Send the built payload to the sub group order endpoint. Isolated one-liner so the
// "sub group orders go to add-venue-sub-group-order, never the single-store add-order"
// contract can be asserted directly in tests (mock @/api/product and check which fired).
export function placeSubGroupOrder(data) {
  return addVenueSubGroupOrder(data)
}

// Combo add-on de-duplication, mirror of payment/index.vue deleteDuplicate.
// Receives 3 arrays and removes from `arr` any object whose name appears in `brr` or `crr`.
// 接收 3 个数组，去掉 arr 中包含 brr 和 crr 的对象
export function deleteDuplicate(arr, brr, crr) {
  const c = []
  const y = []
  arr.forEach((item) => {
    const check = brr.every(function (b) {
      return item.name !== b.name
    })
    if (check) {
      c.push(item)
    }
  })
  c.forEach((item) => {
    const check = crr.every(function (b) {
      return item.name !== b.name
    })
    if (check) {
      y.push(item)
    }
  })
  return y
}

// Massage the per-venue sub group cart the same way payment/index.vue subGroupCart does:
// for combo products, strip optional add-ons and de-dupe against add/remove lists.
// `rawCartList` is the shape returned by the venueSubGroupItemList getter.
export function massageSubGroupCart(rawCartList) {
  const cartList = rawCartList || {}
  for (const i in cartList) {
    if (cartList[i] && cartList[i].cart) {
      cartList[i].cart = (cartList[i].cart || []).map(item => {
        if (item.comboProducts) {
          return {
            ...item,
            // comboProducts 需要去掉配菜
            comboProducts: deleteDuplicate(
              item.comboProducts.filter(e => e.type !== 'optional'),
              item.addList.filter(a => a.type !== 'oneProduct' && a.type !== 'optional'),
              item.removeList
            ),
            addList: item.addList.filter(a => a.type !== 'oneProduct'),
          }
        }
        return item
      })
    }
  }
  return cartList
}

/**
 * Build the add-venue-sub-group-order request payload for the pay-later flow.
 *
 * Mirrors the isSubgroupPayment shape of payment/index.vue createOrder(): whole-cart
 * amount/subtotal from the sub group getters, venueSubGroupId set, itemList empty,
 * cartList carrying every store's massaged cart.
 *
 * Backend contract: add-venue-sub-group-order reuses the most recent unpaid parent order
 * for this sub group, so callers never branch on new-vs-append — one call covers both and
 * the response id/orderNo is always the parent order's.
 *
 * @param {Object} ctx dependency-injected context (no Vue coupling), fields:
 *   amount, subtotal            — pre-computed from subGroupCartRealPrice / subGroupCartTotal
 *   currency, details, user     — from store state
 *   venueId, venueSubGroupId
 *   cartList                    — already-massaged subGroupCart (massageSubGroupCart output)
 *   menuType
 *   （不收 spDiscounts：合单明确不带优惠码，见下面 spDiscounts 一行）
 *   isSpMode, includeAlcohol
 *
 * A sub group order is always counter/dine-in and never delivery (product decision
 * 2026-07-07). So this builder does not consume isOnline / deliveryInfo — pulling one
 * store's delivery details onto a multi-store order was never a supported scenario.
 * @returns {Object} request payload
 */
export function buildSubGroupOrderPayload(ctx) {
  // Clone details so the mutation below (over18) doesn't write back into Vuex
  // state.details by reference — mirrors payment/index.vue, which clones first.
  const data = {
    amount: ctx.amount,
    subtotal: ctx.subtotal,
    currency: ctx.currency,
    details: { ...ctx.details },
    type: 2,
    user: ctx.user,
    venueId: ctx.venueId,
    venueSubGroupId: ctx.venueSubGroupId,
    itemList: [],
    cartList: ctx.cartList,
    // 合单明确不带优惠码（owner D71-8，2026-10-14）：固定空数组，不管 store 里是否残留单店购物车的券。
    // 注意：后端并不会无视这个字段 —— 合单也会按每家成员店逐个重验券码，成员店有这张券就真的给折扣
    // （AddOrderForm 约 :1561-1647）。所以「发空」本身就是保证：单店带进来的券不会再折扣合单。
    // 后端要不要也强制这条规则，等 owner 看完生产探针再定。
    spDiscounts: [],
    creatorType: 'qr',
    menuType: ctx.menuType
  }
  if (ctx.isSpMode) {
    data.spMode = true
  }
  if (ctx.includeAlcohol) {
    data.details.over18 = true
  }
  return data
}

/**
 * Resolve the post-paid success route for a placed order.
 * Sub group orders land on the vgs order detail page (/vgs/:id/pay/:orderId → the double
 * order-number page); single-store orders land on /v/:venueId/pay/:orderId. This is the
 * one decision that BUG-1 got wrong (it always used the single-store venue route).
 *
 * @returns {{ path: string, query: { orderNo: string } }}
 */
export function resolvePostPaidTarget({ isSubgroupCart, venueId, venueSubGroupId, orderId, orderNo }) {
  if (isSubgroupCart) {
    return { path: `/vgs/${venueSubGroupId}/pay/${orderId}`, query: { orderNo } }
  }
  return { path: `/v/${venueId}/pay/${orderId}`, query: { orderNo } }
}
